import { useCallback } from 'react';
import { usePageKeyboard } from '../../keyboard/state/use-page-keyboard';

enum Direction {
    Left = -1,
    Right = 1
}

type UseImagePreviewNavigationParams = {
    idList: number[];
    currentImageId: number;
    onNavigate: (newImageId: number) => void;
    enabled: boolean;
};

type UseImagePreviewNavigationResult = {
    onSwipeLeft: () => void;
    onSwipeRight: () => void;
    canGoLeft: boolean;
    canGoRight: boolean;
};

function ownsArrowKeys(activeElement: Element | null): boolean {
    return (
        activeElement instanceof HTMLInputElement ||
        activeElement instanceof HTMLTextAreaElement ||
        (activeElement !== null && activeElement.closest('[role="tablist"]') !== null)
    );
}

export function useImagePreviewNavigation({
    idList,
    currentImageId,
    onNavigate,
    enabled
}: UseImagePreviewNavigationParams): UseImagePreviewNavigationResult {
    const stepInGallery = useCallback(
        (direction: Direction) => {
            const currentIndex = idList.indexOf(currentImageId);
            if (currentIndex === -1) return;

            let newIndex = currentIndex;
            if (direction === Direction.Left) {
                newIndex = Math.max(0, currentIndex - 1);
            } else if (direction === Direction.Right) {
                newIndex = Math.min(idList.length - 1, currentIndex + 1);
            }

            const newImageId = idList[newIndex];
            if (newImageId === currentImageId) return;

            onNavigate(newImageId);
        },
        [idList, currentImageId, onNavigate]
    );

    usePageKeyboard({
        onKeyUp: (e) => {
            if (!enabled) return;

            if (ownsArrowKeys(document.activeElement)) return;

            switch (e.key) {
                case 'ArrowLeft':
                    stepInGallery(Direction.Left);
                    break;
                case 'ArrowRight':
                    stepInGallery(Direction.Right);
                    break;
            }
        }
    });

    const onSwipeLeft = useCallback(() => {
        stepInGallery(Direction.Left);
    }, [stepInGallery]);

    const onSwipeRight = useCallback(() => {
        stepInGallery(Direction.Right);
    }, [stepInGallery]);

    const currentIndex = idList.indexOf(currentImageId);
    const canGoLeft = currentIndex > 0;
    const canGoRight = currentIndex !== -1 && currentIndex < idList.length - 1;

    return { onSwipeLeft, onSwipeRight, canGoLeft, canGoRight };
}
